import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ArrowRight,
  CalendarDays,
  Clock3,
  History,
  MonitorPlay,
  Play,
  Plus,
  Radio,
  RefreshCw,
  Settings2,
  Users,
  Video,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert } from '../components/Alert';
import { Avatar } from '../components/Avatar';
import { Badge, RoomStatusBadge } from '../components/Badge';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { Field, Input, Textarea } from '../components/Field';
import { Modal } from '../components/Modal';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { api, errorMessage } from '../lib/api';
import { formatBitrate, formatDate, formatDuration, formatFps, formatResolution } from '../lib/format';
import type { MyRoomsResponse, Room } from '../lib/types';

function firstWord(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function SettingsLine({ room }: { room: Room }) {
  if (!room.settings) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600">
        <Settings2 className="size-3" />
        Параметры появятся после старта
      </span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-wide text-zinc-500">
      <span>{formatResolution(room.settings.width, room.settings.height)}</span>
      <span className="text-zinc-700">·</span>
      <span>{formatFps(room.settings.fps)}</span>
      <span className="text-zinc-700">·</span>
      <span>{formatBitrate(room.settings.bitrateKbps)}</span>
    </div>
  );
}

function ActiveRoomCard({
  room,
  isOwn,
  joining,
  onWatch,
}: {
  room: Room;
  isOwn: boolean;
  joining: boolean;
  onWatch: (room: Room) => void;
}) {
  const full = !isOwn && room.viewerCount >= room.maxViewers;
  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/[0.07] bg-[#0c0e14]/80 p-5 transition-colors hover:border-white/[0.12]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/25 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar name={room.owner.displayName} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-[12px] text-zinc-400">{room.owner.displayName}</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-600">Ведущий</p>
          </div>
        </div>
        <RoomStatusBadge status={room.status} />
      </div>

      <h3 className="mt-4 line-clamp-2 text-[17px] font-medium leading-snug tracking-[-0.015em] text-zinc-100">
        {room.title}
      </h3>
      <p className="mt-1 line-clamp-1 text-[12px] text-zinc-600">
        {room.description || 'Без описания'}
      </p>

      <div className="mt-4 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400">
          <Users className="size-3.5 text-zinc-600" />
          {room.viewerCount} / {room.maxViewers}
        </span>
        {room.settings && (
          <Badge tone="violet" className="font-mono">
            {room.settings.resolutionLabel}
          </Badge>
        )}
      </div>
      <div className="mt-2">
        <SettingsLine room={room} />
      </div>

      <div className="mt-5 flex items-center justify-between gap-2 border-t border-white/[0.06] pt-4">
        <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600">
          <Radio className="size-3" />
          {room.viewerCount === 1 ? '1 зритель' : `${room.viewerCount} зрителя`}
        </span>
        <Button
          size="sm"
          variant={isOwn ? 'secondary' : 'primary'}
          onClick={() => onWatch(room)}
          loading={joining}
          disabled={full || joining}
        >
          {!joining && (isOwn ? <Settings2 className="size-3.5" /> : <Play className="size-3.5" />)}
          {isOwn ? 'Управлять' : full ? 'Заполнена' : 'Смотреть'}
        </Button>
      </div>
    </article>
  );
}

function SectionHeading({
  title,
  count,
  icon: Icon,
  action,
}: {
  title: string;
  count?: number;
  icon: React.ElementType;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-end justify-between gap-4">
      <div className="flex items-center gap-2.5">
        <Icon className="size-4 text-zinc-600" />
        <h2 className="text-[15px] font-medium tracking-[-0.01em] text-zinc-200">{title}</h2>
        {count != null && count > 0 && (
          <span className="rounded-full border border-white/[0.07] bg-white/[0.03] px-2 py-0.5 font-mono text-[10px] text-zinc-500">
            {count}
          </span>
        )}
      </div>
      {action}
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myRooms, setMyRooms] = useState<MyRoomsResponse>({ active: [], history: [] });
  const [activeRooms, setActiveRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollError, setPollError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const aliveRef = useRef(true);

  const loadData = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const [mine, active] = await Promise.all([api.myRooms(), api.activeRooms()]);
      if (!aliveRef.current) return;
      setMyRooms(mine);
      setActiveRooms(active.rooms);
      setError(null);
      setPollError(false);
    } catch (caught) {
      if (!aliveRef.current) return;
      if (initial) setError(errorMessage(caught));
      else setPollError(true);
    } finally {
      if (aliveRef.current && initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    void loadData(true);
    const interval = window.setInterval(() => {
      void loadData(false);
    }, 6000);
    const onFocus = () => {
      void loadData(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void loadData(false);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      aliveRef.current = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadData]);

  const onCreate = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (title.trim().length === 0) {
      setFormError('Введите название комнаты.');
      return;
    }
    setCreating(true);
    try {
      const response = await api.createRoom({
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      setCreateOpen(false);
      setTitle('');
      setDescription('');
      navigate(`/rooms/${response.room.id}`);
    } catch (caught) {
      setFormError(errorMessage(caught));
    } finally {
      setCreating(false);
    }
  };

  const onWatch = async (room: Room) => {
    setActionError(null);
    if (room.owner.id === user?.id) {
      navigate(`/rooms/${room.id}`);
      return;
    }
    setJoiningId(room.id);
    try {
      await api.joinRoom(room.id);
      navigate(`/rooms/${room.id}`);
    } catch (caught) {
      setActionError(errorMessage(caught));
      void loadData(false);
    } finally {
      setJoiningId(null);
    }
  };

  const activeOwnRoom = myRooms.active[0];
  const hasActiveRoom = Boolean(activeOwnRoom);

  return (
    <AppShell>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-zinc-600">
            Личный кабинет
          </p>
          <h1 className="mt-2 text-3xl font-semibold tracking-[-0.04em] text-white sm:text-[34px]">
            Привет, {firstWord(user?.displayName ?? '')}
          </h1>
          <p className="mt-2 text-[13px] text-zinc-500">
            Управляйте своей трансляцией и подключайтесь к активным комнатам.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="md"
            onClick={() => void loadData(false)}
            title="Обновить"
          >
            <RefreshCw className="size-4" />
            <span className="hidden sm:inline">Обновить</span>
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              setFormError(null);
              setCreateOpen(true);
            }}
            disabled={hasActiveRoom}
            title={hasActiveRoom ? 'Сначала завершите текущую трансляцию' : undefined}
          >
            <Plus className="size-4" />
            Создать трансляцию
          </Button>
        </div>
      </div>

      {hasActiveRoom && (
        <Alert tone="info" className="mb-6">
          У вас уже есть активная комната «{activeOwnRoom?.title}». Завершите её, чтобы создать
          новую.{' '}
          <Link
            to={`/rooms/${activeOwnRoom?.id}`}
            className="font-medium text-cyan-200 underline decoration-cyan-400/30 underline-offset-2 hover:text-white"
          >
            Перейти в комнату
          </Link>
        </Alert>
      )}

      {error && (
        <Alert tone="error" title="Не удалось загрузить кабинет" className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <span>{error}</span>
            <Button size="sm" variant="secondary" onClick={() => void loadData(true)}>
              Повторить
            </Button>
          </div>
        </Alert>
      )}

      {actionError && (
        <Alert tone="error" className="mb-6">
          {actionError}
        </Alert>
      )}

      {pollError && !error && (
        <div className="mb-6 flex items-center gap-2 text-[12px] text-amber-300/80">
          <RefreshCw className="size-3.5" />
          Не удалось обновить список комнат. Показаны последние данные.
        </div>
      )}

      <section className="mb-10">
        <SectionHeading
          title="Мои трансляции"
          icon={Video}
          count={myRooms.active.length}
          action={
            !hasActiveRoom ? (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="text-[12px] text-violet-300 transition-colors hover:text-violet-200"
              >
                + Создать
              </button>
            ) : undefined
          }
        />
        {loading ? (
          <div className="h-36 animate-pulse rounded-2xl border border-white/[0.05] bg-white/[0.02]" />
        ) : myRooms.active.length === 0 ? (
          <EmptyState
            icon={MonitorPlay}
            title="У вас пока нет активной трансляции"
            description="Создайте комнату, выберите экран и отправьте ссылку зрителям."
            action={
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                <Plus className="size-4" />
                Создать трансляцию
              </Button>
            }
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {myRooms.active.map((room) => (
              <article
                key={room.id}
                className="flex flex-col rounded-2xl border border-violet-400/15 bg-gradient-to-br from-violet-500/[0.07] to-white/[0.015] p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="flex size-9 items-center justify-center rounded-xl border border-violet-400/20 bg-violet-500/10">
                      <MonitorPlay className="size-4 text-violet-300" />
                    </span>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-violet-300/60">
                        Ваша комната
                      </p>
                      <p className="text-[12px] text-zinc-400">{room.owner.displayName}</p>
                    </div>
                  </div>
                  <RoomStatusBadge status={room.status} />
                </div>
                <h3 className="mt-4 line-clamp-1 text-[16px] font-medium text-zinc-100">{room.title}</h3>
                <p className="mt-1 text-[12px] text-zinc-600">
                  {room.viewerCount} / {room.maxViewers} зрителей
                </p>
                <div className="mt-3">
                  <SettingsLine room={room} />
                </div>
                <Link
                  to={`/rooms/${room.id}`}
                  className="mt-5 inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.05] text-[12px] text-zinc-200 transition-colors hover:bg-white/[0.08]"
                >
                  Открыть комнату
                  <ArrowRight className="size-3.5" />
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="mb-10">
        <SectionHeading
          title="Активные трансляции"
          icon={Radio}
          count={activeRooms.length}
        />
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {[0, 1, 2].map((item) => (
              <div
                key={item}
                className="h-56 animate-pulse rounded-2xl border border-white/[0.05] bg-white/[0.02]"
              />
            ))}
          </div>
        ) : activeRooms.length === 0 ? (
          <EmptyState
            icon={Radio}
            title="Сейчас никто не транслирует экран"
            description="Когда друзья начнут трансляцию, комнаты появятся здесь автоматически."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {activeRooms.map((room) => (
              <ActiveRoomCard
                key={room.id}
                room={room}
                isOwn={room.owner.id === user?.id}
                joining={joiningId === room.id}
                onWatch={(value) => void onWatch(value)}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionHeading title="История" icon={History} count={myRooms.history.length} />
        {loading ? (
          <div className="h-24 animate-pulse rounded-2xl border border-white/[0.05] bg-white/[0.02]" />
        ) : myRooms.history.length === 0 ? (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.012] px-5 py-6 text-[12px] text-zinc-600">
            Завершённые трансляции появятся здесь. Записи видео не сохраняются.
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0c0e14]/70">
            {myRooms.history.map((room, index) => (
              <Link
                key={room.id}
                to={`/rooms/${room.id}`}
                className={`flex flex-col gap-2 px-5 py-4 transition-colors hover:bg-white/[0.025] sm:flex-row sm:items-center sm:justify-between ${
                  index > 0 ? 'border-t border-white/[0.05]' : ''
                }`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-white/[0.06] bg-white/[0.02]">
                    <Video className="size-3.5 text-zinc-600" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-[13px] text-zinc-300">{room.title}</p>
                    <p className="mt-0.5 flex items-center gap-2 text-[10px] text-zinc-600">
                      <CalendarDays className="size-3" />
                      {formatDate(room.createdAt)}
                      <span className="text-zinc-800">·</span>
                      <Clock3 className="size-3" />
                      {formatDuration(room.startedAt, room.endedAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pl-11 sm:pl-0">
                  {room.settings && (
                    <span className="font-mono text-[10px] text-zinc-600">
                      {formatResolution(room.settings.width, room.settings.height)} ·{' '}
                      {formatFps(room.settings.fps)}
                    </span>
                  )}
                  <Badge>Завершена</Badge>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Modal
        open={createOpen}
        onClose={() => {
          if (!creating) setCreateOpen(false);
        }}
        title="Новая трансляция"
        description="Комната создастся в статусе ожидания. Вы станете ведущим."
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={(event) => {
                const form = document.getElementById('create-room-form') as HTMLFormElement | null;
                form?.requestSubmit();
              }}
              loading={creating}
            >
              <Plus className="size-4" />
              Создать комнату
            </Button>
          </>
        }
      >
        <form id="create-room-form" onSubmit={onCreate} className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}
          <Field label="Название комнаты" htmlFor="room-title" hint={`${title.length}/80`}>
            <Input
              id="room-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Например, Разбор дизайна"
              maxLength={80}
              autoFocus
              required
            />
          </Field>
          <Field
            label="Краткое описание"
            htmlFor="room-description"
            hint="Необязательно"
          >
            <Textarea
              id="room-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Что будет на экране?"
              maxLength={500}
            />
          </Field>
        </form>
      </Modal>
    </AppShell>
  );
}
